import classnames from 'classnames/bind'
import React, { useState } from 'react'
import { useTranslate } from 'react-polyglot'

import Link, { LinkProps } from '~/components/Link'

import { useStyle } from '~/providers/StyleProvider'

import Select from '../Form/Select'
import InlineCta from '../InlineCta'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface DepartmentProps {
  id?: string
  name?: string
}

export interface JobProps {
  id?: string
  name?: string
  url?: LinkProps
  timezone?: string
  department?: DepartmentProps[]
}

export interface FilterProp {
  id?: string
  name?: string
  jobs?: string[]
}

export interface JobsProps {
  className?: string
  items?: JobProps[]
  filters?: Record<string, FilterProp>
}

function Jobs({ className, items, filters }: JobsProps) {
  const t = useTranslate()
  const [currentFilter, setCurrentFilter] = useState('all')

  const gridStyle = useStyle({ grid: 'base-grid' })
  const filterNameStyle = useStyle({ textPreset: 'title-heading-4' })
  const filterJobStyle = useStyle({ textPreset: 'cta-label' })
  const timezoneStyle = useStyle({ textPreset: 'text-body' })

  const renderFilterSection = (filter) => {
    return (
      <div className={css.filterSection} key={filter?.id}>
        <div className={cx(css.filterName, filterNameStyle)}>
          {filter?.name}
        </div>
        <div className={cx(css.filterJobs)}>
          {filter?.jobs?.map((job) => {
            const { name, url, timezone, id }: JobProps = items?.find(
              (item) => item.id === job,
            )
            return (
              <Link
                className={cx(css.filterJob, filterJobStyle)}
                {...url}
                key={id}>
                <span>{name}</span>
                <span className={timezoneStyle}>{timezone}</span>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  const onFilterChangee = (filter) =>
    setCurrentFilter(filter?.currentTarget?.value)

  return (
    <div className={cx(css.Jobs, className)}>
      <div className={cx(css.grid, gridStyle)}>
        <div className={css.filters}>
          <div className={cx(css.filtersContent, css.hideOnSmallScreen)}>
            <InlineCta
              className={cx(css.filterItem, filterJobStyle)}
              textPreset="cta-label"
              forceUnderline={currentFilter === 'all' ? true : false}
              theme={currentFilter === 'all' ? 'primary' : 'inherit'}
              lineProps={{
                initialLineState:
                  currentFilter === 'all' ? 'underlined' : 'none',
                thickness: 2,
              }}
              type="left_center"
              onClick={() => setCurrentFilter('all')}>
              {t('jobs.all_filters')}&nbsp;({items?.length})
            </InlineCta>
            {Object.values(filters)?.map((filter) => (
              <InlineCta
                key={filter?.id}
                className={cx(css.filterItem, filterJobStyle)}
                textPreset="cta-label"
                forceUnderline={currentFilter === filter.id ? true : false}
                theme={currentFilter === filter.id ? 'primary' : 'inherit'}
                lineProps={{
                  initialLineState:
                    currentFilter === filter.id ? 'underlined' : 'none',
                  thickness: 2,
                }}
                type="left_center"
                onClick={() => setCurrentFilter(filter.id)}>
                {filter?.name}&nbsp;({filter.jobs.length})
              </InlineCta>
            ))}
          </div>
          <div className={css.hideOnLargeScreen}>
            <Select
              onChange={onFilterChangee}
              defaultValue="all"
              textPreset="cta-label"
              layout="rounded"
              options={[
                {
                  value: 'all',
                  label: t('jobs.all_filters'),
                },
                ...Object.values(filters).map((filter) => ({
                  value: filter.id,
                  label: filter.name,
                })),
              ]}
            />
          </div>
        </div>
        <div className={css.jobs}>
          {currentFilter === 'all'
            ? Object.values(filters)?.map((filter) =>
                renderFilterSection(filter),
              )
            : renderFilterSection(filters?.[currentFilter])}
        </div>
      </div>
    </div>
  )
}

Jobs.defaultProps = {}

export default Jobs
